import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { CorrectIcon, EyeIcon, EyeOffIcon, LinkCopyIcon } from '../../../../../ui/Icons';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  BankAccountFormUIModel,
  BankAccountUIModel
} from '../../../../../shared/ui-models/landlord.ui-model';
import { isBankAccountSaveError, saveBankAccount } from '../../../../data.service';

export interface BankAccountCardProps {
  config: {
    landlordId: string;
    bankAccount: BankAccountUIModel | null;
  };
  eventHandlers: {
    savedHandler: () => void;
  };
}

const FIELDS: {
  key: keyof BankAccountFormUIModel;
  label: string;
  optional?: boolean;
  placeholder?: string;
}[] = [
  { key: 'bankName', label: 'Bank name' },
  { key: 'accountHolderName', label: 'Account holder name' },
  { key: 'accountNumber', label: 'Account number' },
  { key: 'swiftBic', label: 'SWIFT / BIC', optional: true },
  { key: 'country', label: 'Country', optional: true, placeholder: 'EG' },
  { key: 'currency', label: 'Currency', optional: true, placeholder: 'EGP' }
];

const EMPTY_FORM: BankAccountFormUIModel = {
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  swiftBic: '',
  country: '',
  currency: ''
};

function toForm(bankAccount: BankAccountUIModel | null): BankAccountFormUIModel {
  if (!bankAccount) {
    return { ...EMPTY_FORM };
  }
  return {
    bankName: bankAccount.bankName,
    accountHolderName: bankAccount.accountHolderName,
    accountNumber: bankAccount.accountNumber,
    swiftBic: bankAccount.swiftBic,
    country: bankAccount.country,
    currency: bankAccount.currency
  };
}

function formatTimestamp(value: string): string {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function maskAccountNumber(value: string): string {
  if (!value) {
    return '';
  }
  const last4 = value.slice(-4);
  const masked = '•'.repeat(Math.max(value.length - 4, 0)) + last4;
  return (masked.match(/.{1,4}/g) || [masked]).join(' ');
}

const LABEL = 'text-lg font-gilroy-medium text-gray-600';
const VALUE = 'mt-1 text-xl font-gilroy-bold text-neutral-500';
const ICON_BUTTON =
  'rounded-lg p-2 text-primary-700 transition-colors hover:bg-primary-100 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

const inputClass = (hasError: boolean) =>
  `w-full rounded-lg border px-4 py-3 text-xl font-gilroy-medium text-neutral-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
    hasError ? 'border-scarlet focus:border-scarlet' : 'border-gray-200 focus:border-primary-500'
  }`;

export const BankAccountCard: React.FC<BankAccountCardProps> = ({ config, eventHandlers }) => {
  const { bankAccount, landlordId } = config;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [form, setForm] = useState<BankAccountFormUIModel>(toForm(bankAccount));
  const [fieldErrors, setFieldErrors] = useState<{ [field: string]: string }>({});
  const [formError, setFormError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isAccountRevealed, setIsAccountRevealed] = useState<boolean>(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number>();

  useEffect(() => {
    setIsAccountRevealed(false);
  }, [bankAccount?.accountNumber]);

  useEffect(() => () => window.clearTimeout(copyTimeoutRef.current), []);

  const startEditing = useCallback(() => {
    setForm(toForm(bankAccount));
    setFieldErrors({});
    setFormError('');
    setIsEditing(true);
  }, [bankAccount]);

  const handleChange = useCallback((field: keyof BankAccountFormUIModel, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const copyValue = useCallback((field: string, value: string) => {
    if (!value || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    window.clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = window.setTimeout(() => setCopiedField(null), 1500);
  }, []);

  const handleSave = useCallback(() => {
    setIsSaving(true);
    setFormError('');
    setFieldErrors({});

    saveBankAccount(landlordId, form).then((response) => {
      if (response === true) {
        setIsEditing(false);
        eventHandlers.savedHandler();
      } else if (isBankAccountSaveError(response)) {
        const mapped: { [field: string]: string } = {};
        response.fieldErrors.forEach((error) => {
          mapped[error.field] = error.message;
        });
        setFieldErrors(mapped);
        setFormError(response.message);
      } else if (isErrorInfo(response)) {
        setFormError(response.errorMessage || 'The bank account could not be saved');
      }
      setIsSaving(false);
    });
  }, [landlordId, form, eventHandlers]);

  const copyAnnouncement = copiedField
    ? `${copiedField === 'swiftBic' ? 'SWIFT / BIC' : 'Account number'} copied`
    : '';

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h2 className="m-0 text-2xl font-gilroy-bold text-neutral-500">Bank account</h2>
        {!isEditing && bankAccount && (
          <Button type={ButtonType.default} size={ButtonSize.small} onClick={startEditing}>
            Edit bank account
          </Button>
        )}
      </div>

      <p aria-live="polite" className="sr-only">
        {copyAnnouncement}
      </p>

      {!isEditing && !bankAccount && (
        <div className="rounded-xl border border-secondary-100 bg-secondary-50 p-6">
          <p className="m-0 text-xl font-gilroy-bold text-secondary-700">No bank account on file</p>
          <div className="mt-5">
            <Button type={ButtonType.primary} size={ButtonSize.medium} onClick={startEditing}>
              Add bank account
            </Button>
          </div>
        </div>
      )}

      {!isEditing && bankAccount && (
        <div className="flex flex-col gap-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <div className={LABEL}>Bank name</div>
              <div className={VALUE}>{bankAccount.bankName || '\u2014'}</div>
            </div>
            <div>
              <div className={LABEL}>Account holder</div>
              <div className={VALUE}>{bankAccount.accountHolderName || '\u2014'}</div>
            </div>
          </div>

          <div className="rounded-xl border border-primary-100 bg-primary-50 p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-lg font-gilroy-medium text-primary-700">Account number</div>
                <div className="mt-1 truncate font-mono text-xl tracking-wider text-neutral-500">
                  {bankAccount.accountNumber
                    ? isAccountRevealed
                      ? bankAccount.accountNumber
                      : maskAccountNumber(bankAccount.accountNumber)
                    : '\u2014'}
                </div>
              </div>
              {bankAccount.accountNumber && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className={ICON_BUTTON}
                    aria-label={isAccountRevealed ? 'Hide account number' : 'Show account number'}
                    aria-pressed={isAccountRevealed}
                    onClick={() => setIsAccountRevealed((current) => !current)}
                  >
                    {isAccountRevealed ? <EyeOffIcon size="20px" /> : <EyeIcon size="20px" />}
                  </button>
                  <button
                    type="button"
                    className={ICON_BUTTON}
                    aria-label="Copy account number"
                    onClick={() => copyValue('accountNumber', bankAccount.accountNumber)}
                  >
                    {copiedField === 'accountNumber' ? (
                      <CorrectIcon size="20px" />
                    ) : (
                      <LinkCopyIcon size="20px" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {bankAccount.swiftBic && (
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-primary-100 pt-4">
                <div className="min-w-0">
                  <div className="text-lg font-gilroy-medium text-primary-700">SWIFT / BIC</div>
                  <div className="mt-1 truncate font-mono text-xl tracking-wider text-neutral-500">
                    {bankAccount.swiftBic}
                  </div>
                </div>
                <button
                  type="button"
                  className={`shrink-0 ${ICON_BUTTON}`}
                  aria-label="Copy SWIFT / BIC"
                  onClick={() => copyValue('swiftBic', bankAccount.swiftBic)}
                >
                  {copiedField === 'swiftBic' ? (
                    <CorrectIcon size="20px" />
                  ) : (
                    <LinkCopyIcon size="20px" />
                  )}
                </button>
              </div>
            )}
          </div>

          {(bankAccount.country || bankAccount.currency) && (
            <div className="flex flex-wrap gap-2">
              {bankAccount.country && (
                <span className="rounded-full bg-creams-200 px-4 py-1.5 text-lg font-gilroy-medium text-neutral-500">
                  {bankAccount.country}
                </span>
              )}
              {bankAccount.currency && (
                <span className="rounded-full bg-creams-200 px-4 py-1.5 text-lg font-gilroy-medium text-neutral-500">
                  {bankAccount.currency}
                </span>
              )}
            </div>
          )}

          {bankAccount.lastUpdatedBy && (
            <div className="text-lg font-gilroy-medium text-gray-600">
              Last updated by {bankAccount.lastUpdatedBy}
              {bankAccount.lastUpdatedAt ? ` on ${formatTimestamp(bankAccount.lastUpdatedAt)}` : ''}
            </div>
          )}
        </div>
      )}

      {isEditing && (
        <div className="flex flex-col gap-5">
          {formError && (
            <div
              role="alert"
              className="rounded-lg border border-scarlet bg-red-50 px-4 py-3 text-lg font-gilroy-medium text-scarlet"
            >
              {formError}
            </div>
          )}

          <div className="grid gap-5 md:grid-cols-2">
            {FIELDS.map((field) => {
              const inputId = `bank-account-${landlordId}-${field.key}`;
              return (
                <div key={field.key}>
                  <label htmlFor={inputId} className={`mb-2 block ${LABEL}`}>
                    {field.label}
                    {field.optional ? ' (optional)' : ''}
                  </label>
                  <input
                    id={inputId}
                    className={inputClass(!!fieldErrors[field.key])}
                    placeholder={field.placeholder}
                    value={form[field.key]}
                    aria-invalid={!!fieldErrors[field.key]}
                    aria-describedby={fieldErrors[field.key] ? `${inputId}-error` : undefined}
                    onChange={(event) => handleChange(field.key, event.target.value)}
                  />
                  {fieldErrors[field.key] && (
                    <div id={`${inputId}-error`} className="mt-2 text-lg text-scarlet">
                      {fieldErrors[field.key]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap justify-end gap-3">
            <Button
              type={ButtonType.transparent}
              size={ButtonSize.medium}
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.primary}
              size={ButtonSize.medium}
              disabled={isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Saving\u2026' : 'Save bank account'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BankAccountCard;
